import { useEffect, useState } from "react";
import { ArrowLeft, Save, Plus, X, Link2, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { categoryApi, cityApi, eventApi } from "@/lib/api";
import { toast } from "sonner";

const empty = {
  title: "", description: "", category_key: "", city_key: "",
  venue: "", address: "", image_url: "", images: [], event_date: "", event_time: "",
  price: 0, is_free: false, booking_type: "redirect", booking_url: "",
  contact_number: "", faqs: [], tags: [], is_trending: false, featured: false,
  status: "live",
};

export default function EventForm({ event, onSaved, onCancel }) {
  const [form, setForm] = useState(event ? { ...empty, ...event } : empty);
  const [cats, setCats] = useState([]);
  const [cities, setCities] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newTag, setNewTag] = useState("");
  const [newImg, setNewImg] = useState("");
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    categoryApi.list().then(setCats);
    cityApi.list().then(setCities);
  }, []);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleImport = async () => {
    const url = importUrl.trim();
    if (!url) { toast.error("Paste a BookMyShow, District, Insider or event URL"); return; }
    setImporting(true);
    try {
      const data = await eventApi.scrape(url);
      setForm((p) => ({
        ...p,
        title: data.title || p.title,
        description: data.description || p.description,
        image_url: data.image_url || p.image_url,
        images: (data.images && data.images.length ? data.images : p.images) || [],
        venue: data.venue || p.venue,
        address: data.address || p.address,
        event_date: data.event_date || p.event_date,
        event_time: data.event_time || p.event_time,
        price: data.price ?? p.price,
        is_free: data.is_free ?? p.is_free,
        booking_type: "redirect",
        booking_url: data.booking_url || url,
      }));
      toast.success("Imported! Review and save.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not import that URL");
    } finally { setImporting(false); }
  };

  const addFaq = () => setForm((p) => ({ ...p, faqs: [...p.faqs, { q: "", a: "" }] }));
  const removeFaq = (i) => setForm((p) => ({ ...p, faqs: p.faqs.filter((_, idx) => idx !== i) }));
  const setFaq = (i, key, v) => setForm((p) => ({ ...p, faqs: p.faqs.map((f, idx) => (idx === i ? { ...f, [key]: v } : f)) }));

  const addTag = () => {
    const t = newTag.trim();
    if (!t) return;
    setForm((p) => ({ ...p, tags: [...new Set([...(p.tags || []), t])] }));
    setNewTag("");
  };
  const removeTag = (t) => setForm((p) => ({ ...p, tags: p.tags.filter((x) => x !== t) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.event_date || !form.category_key || !form.city_key) {
      toast.error("Please fill title, date, category and city");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price) || 0,
      };
      if (event) {
        await eventApi.update(event.id, payload);
        toast.success("Event updated");
      } else {
        await eventApi.create(payload);
        toast.success("Event created");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button type="button" onClick={onCancel} className="h-10 w-10 rounded-full border border-black/10 grid place-items-center hover:bg-black hover:text-white transition-colors" data-testid="event-form-back">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <h1 className="font-display text-3xl font-black">{event ? "Edit event" : "Create event"}</h1>
        </div>
        <Button type="submit" disabled={saving} data-testid="event-form-submit" className="h-11 rounded-full bg-[#FA003F] hover:bg-[#C40031] font-bold px-6">
          <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save"}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {!event && (
            <Card>
              <div className="flex items-center gap-2 mb-2">
                <Link2 className="h-4 w-4 text-[#FA003F]" />
                <h3 className="font-display font-black text-lg">Import from URL</h3>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Paste a BookMyShow / District / Insider / any event page URL — we'll auto-fill the fields.
              </p>
              <div className="flex gap-2">
                <Input
                  data-testid="import-url-input"
                  value={importUrl}
                  onChange={(e) => setImportUrl(e.target.value)}
                  placeholder="https://in.bookmyshow.com/events/…"
                  className="h-11 rounded-xl"
                />
                <Button
                  type="button"
                  onClick={handleImport}
                  disabled={importing}
                  data-testid="import-url-btn"
                  className="h-11 rounded-full bg-[#1A1A1A] hover:bg-[#FA003F] font-bold px-5"
                >
                  {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Auto-fill</>}
                </Button>
              </div>
            </Card>
          )}
          <Card>
            <h3 className="font-display font-black text-lg mb-4">Basics</h3>
            <div className="space-y-4">
              <Field label="Title">
                <Input data-testid="event-title-input" value={form.title} onChange={(e) => set("title", e.target.value)} className="h-11 rounded-xl" />
              </Field>
              <Field label="Description">
                <Textarea data-testid="event-desc-input" value={form.description} onChange={(e) => set("description", e.target.value)} className="rounded-xl min-h-32" />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Category">
                  <Select value={form.category_key} onValueChange={(v) => set("category_key", v)}>
                    <SelectTrigger className="h-11 rounded-xl" data-testid="event-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent>
                      {cats.map((c) => <SelectItem key={c.key} value={c.key}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="City">
                  <Select value={form.city_key} onValueChange={(v) => set("city_key", v)}>
                    <SelectTrigger className="h-11 rounded-xl" data-testid="event-city-select"><SelectValue placeholder="Select city" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {cities.map((c) => <SelectItem key={c.key} value={c.key}>{c.name}, {c.state}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Venue"><Input value={form.venue} onChange={(e) => set("venue", e.target.value)} className="h-11 rounded-xl" /></Field>
                <Field label="Address"><Input value={form.address} onChange={(e) => set("address", e.target.value)} className="h-11 rounded-xl" /></Field>
              </div>
              <Field label="Cover image URL">
                <Input data-testid="event-image-input" value={form.image_url} onChange={(e) => set("image_url", e.target.value)} placeholder="https://..." className="h-11 rounded-xl" />
              </Field>
              {form.image_url && <img src={form.image_url} alt="preview" className="w-full h-56 object-cover rounded-xl border border-black/10" />}

              <Field label="Gallery images (carousel)">
                <div className="flex gap-2">
                  <Input value={newImg} onChange={(e) => setNewImg(e.target.value)} placeholder="Paste image URL and click Add" className="h-11 rounded-xl" data-testid="event-gallery-input" />
                  <Button type="button" onClick={() => { const u = newImg.trim(); if (!u) return; set("images", [...(form.images || []), u]); setNewImg(""); }} className="h-11 rounded-full bg-[#1A1A1A] px-5">
                    <Plus className="h-4 w-4 mr-1" /> Add
                  </Button>
                </div>
                {form.images?.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-3">
                    {form.images.map((src, i) => (
                      <div key={i} className="relative group rounded-lg overflow-hidden aspect-square border border-black/10">
                        <img src={src} alt="" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => set("images", form.images.filter((_, idx) => idx !== i))}
                          className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/70 text-white grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </Field>
            </div>
          </Card>

          <Card>
            <h3 className="font-display font-black text-lg mb-4">When</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Event date">
                <Input type="date" value={form.event_date} onChange={(e) => set("event_date", e.target.value)} className="h-11 rounded-xl" data-testid="event-date-input" />
              </Field>
              <Field label="Time">
                <Input value={form.event_time} onChange={(e) => set("event_time", e.target.value)} placeholder="8:00 PM" className="h-11 rounded-xl" />
              </Field>
              <Field label="End date (optional)">
                <Input type="date" value={form.end_date || ""} onChange={(e) => set("end_date", e.target.value)} className="h-11 rounded-xl" />
              </Field>
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-black text-lg">FAQs</h3>
              <Button type="button" onClick={addFaq} variant="outline" className="rounded-full h-9"><Plus className="h-4 w-4 mr-1" /> Add</Button>
            </div>
            <div className="space-y-3">
              {form.faqs.map((f, i) => (
                <div key={i} className="border border-black/10 rounded-xl p-4 space-y-2 relative">
                  <button type="button" onClick={() => removeFaq(i)} className="absolute right-3 top-3 text-gray-400 hover:text-red-600"><X className="h-4 w-4" /></button>
                  <Input placeholder="Question" value={f.q} onChange={(e) => setFaq(i, "q", e.target.value)} className="h-10 rounded-lg" />
                  <Textarea placeholder="Answer" value={f.a} onChange={(e) => setFaq(i, "a", e.target.value)} className="rounded-lg min-h-20" />
                </div>
              ))}
              {form.faqs.length === 0 && <div className="text-sm text-gray-500">No FAQs yet.</div>}
            </div>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <h3 className="font-display font-black text-lg mb-4">Booking</h3>
            <div className="space-y-4">
              <ToggleRow
                label="Free event"
                checked={form.is_free}
                onChange={(v) => { set("is_free", v); if (v) set("price", 0); }}
                testid="event-isfree-toggle"
              />
              {!form.is_free && (
                <Field label="Price (₹)">
                  <Input type="number" min={0} value={form.price} onChange={(e) => set("price", e.target.value)} className="h-11 rounded-xl" data-testid="event-price-input" />
                </Field>
              )}

              <Field label="Booking method">
                <div className="grid grid-cols-2 gap-2">
                  {["redirect", "contact"].map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => set("booking_type", v)}
                      data-testid={`booking-type-${v}`}
                      className={`h-11 rounded-full text-sm font-bold border transition-colors capitalize ${
                        form.booking_type === v ? "bg-[#FA003F] text-white border-transparent" : "bg-white text-[#1A1A1A] border-black/10 hover:border-[#FA003F]/40"
                      }`}
                    >
                      {v === "redirect" ? "Redirect URL" : "Contact number"}
                    </button>
                  ))}
                </div>
              </Field>

              <Field label="Booking URL"><Input value={form.booking_url} onChange={(e) => set("booking_url", e.target.value)} placeholder="https://partner.com/book" className="h-11 rounded-xl" data-testid="event-booking-url" /></Field>
              <Field label="Contact number"><Input value={form.contact_number} onChange={(e) => set("contact_number", e.target.value)} placeholder="+91 98765 43210" className="h-11 rounded-xl" data-testid="event-contact-number" /></Field>
            </div>
          </Card>

          <Card>
            <h3 className="font-display font-black text-lg mb-4">Extras</h3>
            <div className="space-y-4">
              <ToggleRow label="Trending" checked={form.is_trending} onChange={(v) => set("is_trending", v)} testid="event-trending-toggle" />
              <ToggleRow label="Featured on hero" checked={form.featured} onChange={(v) => set("featured", v)} testid="event-featured-toggle" />
              <ToggleRow label="Live (published)" checked={form.status === "live"} onChange={(v) => set("status", v ? "live" : "draft")} testid="event-status-toggle" />
              <Field label="Tags">
                <div className="flex gap-2">
                  <Input value={newTag} onChange={(e) => setNewTag(e.target.value)} placeholder="e.g. Music" className="h-10 rounded-full" />
                  <Button type="button" onClick={addTag} className="rounded-full h-10 bg-[#1A1A1A]">Add</Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {form.tags?.map((t) => (
                    <span key={t} className="inline-flex items-center gap-1 rounded-full bg-[#FA003F]/10 text-[#FA003F] text-xs font-bold px-3 py-1">
                      {t}
                      <button type="button" onClick={() => removeTag(t)} className="hover:text-red-600"><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
              </Field>
            </div>
          </Card>
        </div>
      </div>
    </form>
  );
}

function Card({ children }) {
  return <div className="bg-white rounded-2xl border border-black/10 p-6">{children}</div>;
}
function Field({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-gray-500">{label}</Label>{children}</div>;
}
function ToggleRow({ label, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between">
      <Label className="text-sm">{label}</Label>
      <Switch data-testid={testid} checked={checked} onCheckedChange={onChange} className="data-[state=checked]:bg-[#FA003F]" />
    </div>
  );
}
